import type {
  AdminFacilityProposal,
  AdminFacilityProposalListResponse,
  AdminFacilityProposalResponse,
  ConvertFacilityProposalRequest,
  FacilityProposalStatus,
} from '@mds/shared/types/proposal';

import { api } from '@/lib/api';

/**
 * Facility proposals — see `backend/docs/proposals.md`.
 *
 * Every write carries the version it was decided against. A stale one comes
 * back `PROPOSAL_STATE_CHANGED`, and the answer is to reload, never to retry.
 */

/** A type alias, so it carries the index signature the client's `query` needs. */
export type ProposalQuery = {
  page?: number;
  limit?: number;
  status?: FacilityProposalStatus;
  resourceId?: string;
  /** Reference, contact name or email, organization, purpose. */
  search?: string;
  sort?: 'createdAt' | 'bookingDate' | 'reference';
  direction?: 'asc' | 'desc';
};

export const listProposals = (query: ProposalQuery = {}) =>
  api.get<AdminFacilityProposalListResponse>('admin/proposals', { query });

export const getProposal = (proposalId: string): Promise<AdminFacilityProposal> =>
  api.get<AdminFacilityProposalResponse>(`admin/proposals/${proposalId}`).then((r) => r.data);

const transition =
  (action: 'review' | 'await-payment') =>
  (proposalId: string, expectedVersion: number): Promise<AdminFacilityProposal> =>
    api
      .post<AdminFacilityProposalResponse>(`admin/proposals/${proposalId}/${action}`, {
        expectedVersion,
      })
      .then((r) => r.data);

/** `submitted → under_review`. */
export const startReview = transition('review');

/**
 * Rechecks the room, capacity, hours, availability and price, and stores a
 * quote. On a proposal already awaiting payment it refreshes that quote. It
 * never holds the facility.
 */
export const awaitPayment = transition('await-payment');

/** Terminal. The reason is emailed to the guest. */
export const rejectProposal = (
  proposalId: string,
  expectedVersion: number,
  reason: string,
): Promise<AdminFacilityProposal> =>
  api
    .post<AdminFacilityProposalResponse>(`admin/proposals/${proposalId}/reject`, {
      expectedVersion,
      reason,
    })
    .then((r) => r.data);

/**
 * Records the first payment and creates the booking in one transaction. The
 * quote's version and total go back unchanged; the payment is exactly the
 * deposit or exactly the total.
 */
export const convertProposal = (
  proposalId: string,
  body: ConvertFacilityProposalRequest,
): Promise<AdminFacilityProposal> =>
  api
    .post<AdminFacilityProposalResponse>(`admin/proposals/${proposalId}/convert`, body)
    .then((r) => r.data);
